import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Container,
} from '@mui/material';
import {
  Add,
  CloudUpload,
  Description,
  Visibility,
  Delete,
  CheckCircle,
  Warning,
  Schedule,
  Cancel,
  Upload,
  AttachFile,
  Image,
  PictureAsPdf,
  InsertDriveFile,
  CloudDone,
  Error,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { documentsAPI, policiesAPI, claimsAPI } from 'services/api';

const documentTypes = [
  { value: 'policy_application', label: 'Policy Application' },
  { value: 'farm_registration', label: 'Farm Registration' },
  { value: 'identity_proof', label: 'Identity Proof' },
  { value: 'address_proof', label: 'Address Proof' },
  { value: 'bank_statement', label: 'Bank Statement' },
  { value: 'farm_photos', label: 'Farm Photos' },
  { value: 'pond_construction', label: 'Pond Construction Certificate' },
  { value: 'water_quality', label: 'Water Quality Report' },
  { value: 'claim_evidence', label: 'Claim Evidence' },
  { value: 'medical_report', label: 'Veterinary/Medical Report' },
  { value: 'loss_assessment', label: 'Loss Assessment Report' },
  { value: 'geospatial_image', label: 'Geospatial/Satellite Image' },
  { value: 'other', label: 'Other Documents' },
];

const getDocumentIcon = (fileName, documentType) => {
  const extension = fileName?.split('.').pop()?.toLowerCase();
  
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(extension)) {
    return <Image color="primary" />;
  } else if (extension === 'pdf') {
    return <PictureAsPdf color="error" />;
  } else {
    return <InsertDriveFile color="action" />;
  }
};

const DocumentsManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [claims, setClaims] = useState([]);
  
  // Dialog states
  const [uploadDialog, setUploadDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  // Upload states
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  
  // Form states
  const [formData, setFormData] = useState({
    document_type: '',
    policy_id: '',
    claim_id: '',
    title: '',
    description: '',
    files: []
  });
  
  // Filter states
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Notification
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [policiesResponse, claimsResponse] = await Promise.all([
        policiesAPI.getAll(),
        claimsAPI.getAll()
      ]);

      // Filter for user's data
      const userPolicies = policiesResponse.data;
      const userClaims = claimsResponse.data;
      
      // Get documents
      const documentsResponse = await documentsAPI.getMyDocuments();
      const documents = documentsResponse.data;
      
      setPolicies(userPolicies);
      setClaims(userClaims);
      setDocuments(documents);
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setSnackbar({ 
          open: true, 
          message: `File ${file.name} is too large. Maximum size is 10MB.`, 
          severity: 'error' 
        });
        return false;
      }
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setSnackbar({ 
          open: true, 
          message: `File ${file.name} type not supported.`, 
          severity: 'error' 
        });
        return false;
      }
      return true;
    });
    
    setSelectedFiles(validFiles);
    setFormData({ ...formData, files: validFiles });
  };

  const handleUpload = async () => {
    try {
      if (!formData.document_type || selectedFiles.length === 0) {
        setSnackbar({ open: true, message: 'Please select document type and files', severity: 'error' });
        return;
      }

      setUploading(true);
      setUploadProgress(0);

      // Create FormData for file upload
      const uploadData = new FormData();
      uploadData.append('document_type', formData.document_type);
      uploadData.append('title', formData.title || selectedFiles[0].name);
      uploadData.append('description', formData.description);
      
      // Handle geospatial images differently
      if (formData.document_type === 'geospatial_image') {
        // For geospatial images, use the special endpoint
        uploadData.append('file', selectedFiles[0]); // Only single file for geospatial
        uploadData.append('document_type', 'geospatial_image');
        uploadData.append('description', 'Geospatial/Satellite image for pond');
      } else {
        // For regular documents, use the standard endpoint
        if (formData.policy_id) {
          uploadData.append('policy_id', formData.policy_id);
        }
        if (formData.claim_id) {
          uploadData.append('claim_id', formData.claim_id);
        }
        
        // Add files
        selectedFiles.forEach((file, index) => {
          uploadData.append('files', file);
        });
      }

      // Simulate progress (replace with actual upload progress)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Use the correct API endpoint based on document type
      if (formData.document_type === 'geospatial_image') {
        await documentsAPI.uploadGeospatial(uploadData);
      } else {
        await documentsAPI.upload(uploadData);
      }
      
      setUploadProgress(100);
      clearInterval(progressInterval);

      // Refresh documents
      await loadData();
      
      // Reset form and close dialog
      setUploadDialog(false);
      setSelectedFiles([]);
      setFormData({
        document_type: '',
        policy_id: '',
        claim_id: '',
        title: '',
        description: '',
        files: []
      });
      
      setSnackbar({ 
        open: true, 
        message: 'Documents uploaded successfully', 
        severity: 'success' 
      });

    } catch (err) {
      console.error('Error uploading documents:', err);
      setSnackbar({ 
        open: true, 
        message: 'Failed to upload documents. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (documentId) => {
    try {
      await documentsAPI.delete(documentId);
      await loadData();
      setSnackbar({ 
        open: true, 
        message: 'Document deleted successfully', 
        severity: 'success' 
      });
    } catch (err) {
      console.error('Error deleting document:', err);
      setSnackbar({ 
        open: true, 
        message: 'Failed to delete document', 
        severity: 'error' 
      });
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'warning', label: 'Pending Review', icon: <Schedule /> },
      verified: { color: 'success', label: 'Verified', icon: <CheckCircle /> },
      rejected: { color: 'error', label: 'Rejected', icon: <Cancel /> },
      expired: { color: 'default', label: 'Expired', icon: <Warning /> }
    };
    
    const config = statusConfig[status] || { color: 'default', label: status, icon: null };
    return (
      <Chip 
        label={config.label} 
        color={config.color} 
        size="small" 
        icon={config.icon}
      />
    );
  };

  const filteredDocuments = documents.filter(doc => {
    if (filterType !== 'all' && doc.document_type !== filterType) return false;
    if (filterStatus !== 'all' && doc.status !== filterStatus) return false;
    return true;
  });

  const documentStats = {
    total: documents.length,
    pending: documents.filter(d => d.status === 'pending').length,
    verified: documents.filter(d => d.status === 'verified').length,
    rejected: documents.filter(d => d.status === 'rejected').length,
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          variant="contained"
          startIcon={<CloudUpload />}
          onClick={() => setUploadDialog(true)}
          size="large"
        >
          Upload Documents
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" gutterBottom>
                Total Documents
              </Typography>
              <Typography variant="h4" component="div" color="primary">
                {documentStats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" gutterBottom>
                Pending Review
              </Typography>
              <Typography variant="h4" component="div" color="warning.main">
                {documentStats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" gutterBottom>
                Verified
              </Typography>
              <Typography variant="h4" component="div" color="success.main">
                {documentStats.verified}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" gutterBottom>
                Rejected
              </Typography>
              <Typography variant="h4" component="div" color="error.main">
                {documentStats.rejected}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Document Type</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                label="Document Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                {documentTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Documents Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Document</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Related To</TableCell>
                <TableCell>Upload Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDocuments.map((document) => (
                <TableRow key={document.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {getDocumentIcon(document.file_name, document.document_type)}
                      <Box ml={1}>
                        <Typography variant="body2" fontWeight="medium">
                          {document.title || document.file_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {document.file_size ? `${(document.file_size / 1024).toFixed(1)} KB` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {documentTypes.find(t => t.value === document.document_type)?.label || document.document_type}
                  </TableCell>
                  <TableCell>
                    {document.policy_id && <Chip label={`Policy #${document.policy_id}`} size="small" />}
                    {document.claim_id && <Chip label={`Claim #${document.claim_id}`} size="small" />}
                  </TableCell>
                  <TableCell>{new Date(document.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>{getStatusChip(document.status)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View">
                      <IconButton 
                        color="primary"
                        onClick={() => {
                          setSelectedDocument(document);
                          setViewDialog(true);
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        color="error"
                        onClick={() => handleDelete(document.id)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filteredDocuments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      No documents found. Upload your first document to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onClose={() => setUploadDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Upload Documents</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={formData.document_type}
                  onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                  label="Document Type"
                >
                  {documentTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter document title"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Related Policy</InputLabel>
                <Select
                  value={formData.policy_id}
                  onChange={(e) => setFormData({ ...formData, policy_id: e.target.value })}
                  label="Related Policy"
                >
                  <MenuItem value="">None</MenuItem>
                  {policies.map((policy) => (
                    <MenuItem key={policy.id} value={policy.id}>
                      {policy.policy_number} - {policy.plan}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Related Claim</InputLabel>
                <Select
                  value={formData.claim_id}
                  onChange={(e) => setFormData({ ...formData, claim_id: e.target.value })}
                  label="Related Claim"
                >
                  <MenuItem value="">None</MenuItem>
                  {claims.map((claim) => (
                    <MenuItem key={claim.id} value={claim.id}>
                      {claim.claim_number} - {claim.claim_type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter document description (optional)"
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  },
                }}
                onClick={() => document.getElementById('file-input').click()}
              >
                <input
                  id="file-input"
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt"
                />
                <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Click to select files
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supported formats: JPG, PNG, PDF, DOC, TXT (Max 10MB each)
                </Typography>
              </Box>
            </Grid>

            {selectedFiles.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Files:
                </Typography>
                <List dense>
                  {selectedFiles.map((file, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        {getDocumentIcon(file.name)}
                      </ListItemIcon>
                      <ListItemText
                        primary={file.name}
                        secondary={`${(file.size / 1024).toFixed(1)} KB`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => {
                            const newFiles = selectedFiles.filter((_, i) => i !== index);
                            setSelectedFiles(newFiles);
                            setFormData({ ...formData, files: newFiles });
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Grid>
            )}

            {uploading && (
              <Grid item xs={12}>
                <Box sx={{ width: '100%' }}>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                    Uploading... {uploadProgress}%
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpload} 
            variant="contained"
            disabled={uploading || selectedFiles.length === 0 || !formData.document_type}
            startIcon={uploading ? <CircularProgress size={20} /> : <Upload />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Document Details</DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Title:</Typography>
                <Typography variant="body1">{selectedDocument.title}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Type:</Typography>
                <Typography variant="body1">
                  {documentTypes.find(t => t.value === selectedDocument.document_type)?.label}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Status:</Typography>
                {getStatusChip(selectedDocument.status)}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Upload Date:</Typography>
                <Typography variant="body1">
                  {new Date(selectedDocument.created_at).toLocaleString()}
                </Typography>
              </Grid>
              {selectedDocument.description && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Description:</Typography>
                  <Typography variant="body1">{selectedDocument.description}</Typography>
                </Grid>
              )}
              {selectedDocument.verification_notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Verification Notes:</Typography>
                  <Typography variant="body1">{selectedDocument.verification_notes}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DocumentsManagement;